import { message } from 'antd'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { MeContext } from '../../api'
import { isNonAdminSupplier } from '../../api/utils/nonAdminSupplier'

/**
 * #7701 — closes the hospital route families (`/analytics/*`, `/strategic-sourcing/*`, `/enrich/*`,
 * `/aperture/*`) to a supplier-typed NON-ADMIN, so route reachability agrees with the nav.
 *
 * Denial behaviour mirrors `SupplierPortalGuardedRoute` exactly — keyed `message.warn` + `navigate('/')`
 * fired from inside the denial effect (never the render body, react-conventions §11.2), and `<></>` on a
 * denied frame so the wrapped page never mounts, fires no query and pulls no lazy chunk. It carries its
 * OWN toast key so the two guards can never dedupe or overwrite each other's message.
 *
 * Two deliberate differences from that guard:
 *  - **Not admin-blind.** The `!isAdmin` term is load-bearing: `adminNavSections` offers admins Reports
 *    at `/analytics/reports/console`, inside a wrapped family. Dropping the term would lock an admin on
 *    a supplier-typed customer out of their own nav destination.
 *  - **No flag read.** The nav rule keys off `customer.type` alone, and the route half must be governed
 *    by the same condition or the two drift apart again.
 *
 * Polarity on an unknown `me` frame: HOLD — render nothing, no toast, no redirect. Acting on unknown
 * would toast-and-redirect hospital users and admins on any frame where `me` was briefly unresolved,
 * while holding is already fail-safe for the supplier (the guarded page never mounts either way).
 * The predicate is the shared `isNonAdminSupplier` (`api/utils/nonAdminSupplier.ts`, #7907).
 */
export const NonSupplierGuardedRoute: React.FC<{ children?: React.ReactNode }> = props => {
  const { children: Component } = props
  const navigate = useNavigate()
  const { data: meData } = React.useContext(MeContext)

  const isNotAccessible = isNonAdminSupplier(meData?.me)

  React.useEffect(() => {
    if (isNotAccessible) {
      message.warn({
        key: 'non-supplier-guard-route-warn',
        content: 'You do not have permission to visit this page',
      })
      navigate('/')
    }
  }, [isNotAccessible, navigate])

  return !!meData?.me && !isNotAccessible ? <>{Component}</> : <></>
}
